import moment from 'moment-timezone'
import { ShipperStrikePriceOverride } from 'apps/marketplace/apiTypes/shipperStrikePriceOverrides'
import { ShipperStrikePrices } from 'apps/marketplace/apiTypes/shipperStrikePrices'
import { SpotBiddingRule } from 'apps/marketplace/apiTypes/spotBiddingConfigs'
import { REGIONS } from 'constants/countriesStates'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { findEquipment } from 'constants/equipment'
import {
  MILEAGE_CITY_MIN,
  MILEAGE_CITY_MAX,
  MILEAGE_SHORT_MIN,
  MILEAGE_SHORT_MAX,
  MILEAGE_MID_MIN,
  MILEAGE_MID_MAX,
  MILEAGE_TWEENER_MIN,
  MILEAGE_TWEENER_MAX,
  MILEAGE_LONG_MIN,
  MILEAGE_LONG_MAX,
  MILEAGE_CROSSCOUNTRY_MIN,
} from '../constants/shipperStrikePrices'
import { MILEAGE_BUCKETS } from '../types/shipperStrikePrices'

interface IRange {
  rangeStart: number
  rangeEnd?: number
}

export const formatDate = (date: Date) => moment(date).utc().format(FORMATS.yearFirst)

// startAt and endAt override values are stored as UTC dates with midnight
// time values. This means by default they will be displayed in the Eastern
// time zone as the previous day, because of the time difference. This
// function corrects for the current timezone by only using year, month
// and day to construct the final Date object:
export const correctForTimeZone = (date: Date): Date =>
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

export const formatAsPercentage = (n?: number | null): string => `${Math.floor((n || 0) * 100)}%`

export const formatOverridePercentage = (n?: number): string => `${100 + (n || 0) * 100}%`

export const formatEquipmentInfo = (shipperStrikePrice: ShipperStrikePrices): string => {
  const haulType = shipperStrikePrice.isShorthaul ? 'SH' : 'LH'
  const equipmentType = findEquipment(shipperStrikePrice?.equipment?.parentId).name
  return `${haulType} - ${equipmentType}`
}

export const formatStrikePrice = (n?: number | null): string => {
  if (n === null || n === undefined) return DISPLAY.empty
  // Strike price display value is calculated by
  // subtracting 1 from the received float value
  // and converting to percentage. Examples:
  //  - 1.1  -> 0.1  -> +10%
  //  - 1.01 -> 0.01 -> +1%
  //  - 0.8  -> -0.2 -> -20%
  const adjustedValue = Number((n - 1).toFixed(2))
  return `${adjustedValue < 0 ? '' : '+'}${formatAsPercentage(adjustedValue)}`
}

export const formatOverrideDateRange = (override: ShipperStrikePriceOverride | null): string => {
  if (!override) return ''
  const { endAt, startAt } = override
  const format = (dateStr: string) => moment(dateStr).utc().format(FORMATS.dayShort)
  if (startAt && endAt) {
    const [start, end] = [format(startAt), format(endAt)]
    return start === end ? start : `${start} - ${end}`
  }
  if (startAt) return `Since ${format(startAt)}`
  if (endAt) return `Until ${format(endAt)}`
  return ''
}

export const percentageToFloat = (n: number): number => n / 100

export const floatToPercentage = (n: number): number => Number((n * 100).toFixed(2))

export const getMileageBucketFromRange = (rangeEnd?: number): MILEAGE_BUCKETS => {
  if (typeof rangeEnd !== 'number') return MILEAGE_BUCKETS.CROSSCOUNTRY
  if (rangeEnd <= MILEAGE_CITY_MAX) return MILEAGE_BUCKETS.CITY
  if (rangeEnd <= MILEAGE_SHORT_MAX) return MILEAGE_BUCKETS.SHORT
  if (rangeEnd <= MILEAGE_MID_MAX) return MILEAGE_BUCKETS.MID
  if (rangeEnd <= MILEAGE_TWEENER_MAX) return MILEAGE_BUCKETS.TWEENER
  if (rangeEnd <= MILEAGE_LONG_MAX) return MILEAGE_BUCKETS.LONG
  return MILEAGE_BUCKETS.CROSSCOUNTRY
}

export const getRangeFromMileageBucket = (bucket?: MILEAGE_BUCKETS): IRange => {
  switch (bucket) {
    case MILEAGE_BUCKETS.CITY: {
      return {
        rangeStart: MILEAGE_CITY_MIN,
        rangeEnd: MILEAGE_CITY_MAX,
      }
    }
    case MILEAGE_BUCKETS.SHORT: {
      return {
        rangeStart: MILEAGE_SHORT_MIN,
        rangeEnd: MILEAGE_SHORT_MAX,
      }
    }
    case MILEAGE_BUCKETS.MID: {
      return {
        rangeStart: MILEAGE_MID_MIN,
        rangeEnd: MILEAGE_MID_MAX,
      }
    }
    case MILEAGE_BUCKETS.TWEENER: {
      return {
        rangeStart: MILEAGE_TWEENER_MIN,
        rangeEnd: MILEAGE_TWEENER_MAX,
      }
    }
    case MILEAGE_BUCKETS.LONG: {
      return {
        rangeStart: MILEAGE_LONG_MIN,
        rangeEnd: MILEAGE_LONG_MAX,
      }
    }
    default: {
      return {
        rangeStart: MILEAGE_CROSSCOUNTRY_MIN,
      }
    }
  }
}

interface IStatesToRegionsMap {
  [key: string]: string
}

const statesToRegionsMap = REGIONS.filter(region => region.regionsArray.length === 1).reduce<
  IStatesToRegionsMap
>((statesMap, currentRegion) => {
  currentRegion.statesArray.forEach(state => {
    statesMap[state] = currentRegion.name
  })
  return statesMap
}, {})

interface ISortedRulesByRegion {
  [key: string]: SpotBiddingRule[]
}

// Sort strike price rules into buckets based on their
// associated region:
export const sortRulesByRegion = (rules: SpotBiddingRule[]): ISortedRulesByRegion => {
  return rules.reduce<ISortedRulesByRegion>((sortedRules, currentRule) => {
    const geoCriteriaType = currentRule.geographicCriteria?.criteriaType
    const geoValue = currentRule.geographicCriteria?.value || ''
    const isLane = !!currentRule.laneGeographicCriteria
    const isStepPricing = !!currentRule.countLoadsQuotedCriteria

    if (isStepPricing) {
      return {
        ...sortedRules,
        'Step Pricing': [...(sortedRules['Step Pricing'] || []), currentRule],
      }
    }

    if (isLane) {
      return {
        ...sortedRules,
        Lane: [...(sortedRules.Lane || []), currentRule],
      }
    }

    if (geoCriteriaType === 'REGION') {
      return {
        ...sortedRules,
        [geoValue]: [...(sortedRules[geoValue] || []), currentRule],
      }
    }
    if (geoCriteriaType === 'STATE') {
      const region = statesToRegionsMap[geoValue]
      return {
        ...sortedRules,
        [region]: [...(sortedRules[region] || []), currentRule],
      }
    }
    if (geoCriteriaType === 'CITY, STATE' || geoCriteriaType === 'DAT MARKET') {
      const stateCode = geoValue?.split(', ')[1]
      const region = statesToRegionsMap[stateCode]
      return {
        ...sortedRules,
        [region]: [...(sortedRules[region] || []), currentRule],
      }
    }
    return {
      ...sortedRules,
      'All Regions': [...(sortedRules['All Regions'] || []), currentRule],
    }
  }, {})
}

// Find all regions with an associated DNB rule:
export const getDnbRegions = (rules: SpotBiddingRule[]): string[] => {
  const allDnbRules = rules.filter(rule => rule.ruleAction === 'DONT_BID')
  const regionalDnbRules = allDnbRules.filter(
    rule => rule.geographicCriteria?.criteriaType === 'REGION'
  )
  const dnbRegions = Array.from(
    new Set(regionalDnbRules.map(rule => rule.geographicCriteria?.value || ''))
  )

  return dnbRegions
}
